'use client';

/**
 * WebQALongAnswerCard — Reception fast-tier answer (headline + detail).
 *
 * Shows the short answer (`summary.fast.short`, ~25-30 words) as a lead
 * headline, then the long answer (`summary.fast.long`, ~100-150 words)
 * as the detail beneath it — i.e. card2's text folded in here, so the
 * Reception screen has one answer card instead of two while we
 * experiment with the layout. (`web_qa_short_canvas` is still registered
 * — just not placed in web_reception.py — so it's a one-line revert.)
 * Falls back to `short_answer` / `long_answer` for V1 responses.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useDocsStore } from '@/lib/stores/docs-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { FastTierSourceTag } from './web-qa-fast-source';
import { QA_SECTION, QA_BOX } from './web-qa-chrome';
import { QASkeleton } from './web-qa-skeleton';


export function WebQALongAnswerCard() {
  const T = useCardTypography();
  const response = useDocsStore((s) => s.response);
  const isLoading = useDocsStore((s) => s.isLoading);

  // Fast tier in flight (incl. a follow-up replacing a prior answer) → paint
  // the skeleton instead of a blank pane / stale answer; it swaps to content
  // with no reflow when the response lands.
  if (isLoading) return <QASkeleton label="Answer" lines={4} />;
  if (!response) return null;

  const short = response.summary?.fast?.short ?? response.short_answer ?? '';
  const long = response.summary?.fast?.long ?? response.long_answer ?? '';
  if (!short && !long) return null;

  return (
    <section className={QA_SECTION}>
      <div className="w-full">
        <div className={QA_BOX}>
          <FastTierSourceTag />
          {short && (
            <p className={cn(T.body, 'font-medium text-foreground leading-snug')}>{short}</p>
          )}
          {long && (
            <p className={cn(T.body, 'whitespace-pre-line leading-relaxed text-muted-foreground')}>
              {long}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
